// src/components/FocusBar.tsx
// Summary bar showing urgency counts for the My Work hub.

import { useI18n } from '@react/hooks';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface FocusBarProps {
  overdueCount: number;
  dueThisWeekCount: number;
  awaitingReviewCount: number;
  inProgressCount: number;
  onItemClick?: (filter: string) => void;
  /** Segment id currently applied as a filter (see filterId below), if any.
   *  Clicking the active segment again clears the filter. */
  activeFilter?: string | null;
}

// ---------------------------------------------------------------------------
// Inline SVG icons
// ---------------------------------------------------------------------------

function AlertCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polygon points="10 8 16 12 10 16 10 8" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Stat item
// ---------------------------------------------------------------------------

interface StatItemProps {
  icon: React.ReactNode;
  count: number;
  label: string;
  colorClass: string;
  bgClass: string;
  filterId: string;
  onClick?: (filter: string) => void;
  isActive?: boolean;
}

function StatItem({ icon, count, label, colorClass, bgClass, filterId, onClick, isActive }: StatItemProps) {
  const isZero = count === 0;
  const className = `
    flex items-center gap-3 rounded-lg border px-4 py-3 transition-colors
    ${isZero
      ? 'border-border bg-muted/30 text-muted-foreground'
      : `border-transparent ${bgClass} ${colorClass}`
    }
    ${isActive ? 'ring-2 ring-ring ring-offset-2' : ''}
    ${onClick ? 'cursor-pointer hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2' : ''}
  `;

  const children = (
    <>
      <div className="flex-shrink-0">{icon}</div>
      <div className="min-w-0">
        <p className={`text-2xl font-bold leading-none ${isZero ? 'text-muted-foreground' : ''}`}>
          {count}
        </p>
        <p className={`text-xs mt-1 truncate ${isZero ? 'text-muted-foreground' : 'opacity-80'}`}>
          {label}
        </p>
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={className}
        onClick={() => onClick(filterId)}
        aria-pressed={isActive}
        aria-label={`${count} ${label}`}
      >
        {children}
      </button>
    );
  }

  return (
    <div className={className} aria-label={`${count} ${label}`}>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FocusBar component
// ---------------------------------------------------------------------------

export function FocusBar({
  overdueCount,
  dueThisWeekCount,
  awaitingReviewCount,
  inProgressCount,
  onItemClick,
  activeFilter,
}: FocusBarProps) {
  const { t } = useI18n();

  return (
    <div
      className="grid grid-cols-2 gap-3 sm:grid-cols-4"
      role="region"
      aria-label={t('myWork.focusBar.label')}
    >
      <StatItem
        icon={<AlertCircleIcon className="h-5 w-5" />}
        count={overdueCount}
        label={t('myWork.focusBar.overdue')}
        colorClass="text-destructive"
        bgClass="bg-destructive/10"
        filterId="overdue"
        onClick={onItemClick}
        isActive={activeFilter === 'overdue'}
      />
      <StatItem
        icon={<ClockIcon className="h-5 w-5" />}
        count={dueThisWeekCount}
        label={t('myWork.focusBar.dueThisWeek')}
        colorClass="text-yellow-600 dark:text-yellow-400"
        bgClass="bg-yellow-500/10"
        filterId="due_this_week"
        onClick={onItemClick}
        isActive={activeFilter === 'due_this_week'}
      />
      <StatItem
        icon={<EyeIcon className="h-5 w-5" />}
        count={awaitingReviewCount}
        label={t('myWork.focusBar.awaitingReview')}
        colorClass="text-primary"
        bgClass="bg-primary/10"
        filterId="in_review"
        onClick={onItemClick}
        isActive={activeFilter === 'in_review'}
      />
      <StatItem
        icon={<PlayIcon className="h-5 w-5" />}
        count={inProgressCount}
        label={t('myWork.focusBar.inProgress')}
        colorClass="text-accent-foreground"
        bgClass="bg-accent/50"
        filterId="in_progress"
        onClick={onItemClick}
        isActive={activeFilter === 'in_progress'}
      />
    </div>
  );
}
